/**
 * useContentFlag Hook
 *
 * Custom hook for flagging inappropriate content.
 * Creates a content flag for moderator review.
 */

import { useState, useCallback } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { FlagContentType, FlagReason, ContentFlagCreateData } from '@flash/shared';

/**
 * Return type for useContentFlag hook
 */
interface UseContentFlagResult {
  /** Submit a flag for content */
  submitFlag: (data: {
    contentType: FlagContentType;
    contentId: string;
    gymId: string;
    reason: FlagReason;
    details?: string;
  }) => Promise<void>;
  /** Whether a submission is in progress */
  isSubmitting: boolean;
  /** Error message if submission failed */
  error: string | null;
  /** Success message after submission */
  success: boolean;
  /** Reset the success/error state */
  reset: () => void;
}

/**
 * Hook to flag content for moderation review
 *
 * @returns Object containing submitFlag function and submission state
 *
 * @example
 * ```tsx
 * const { submitFlag, isSubmitting, error, success } = useContentFlag();
 *
 * const handleReport = async () => {
 *   await submitFlag({
 *     contentType: 'comment',
 *     contentId: comment.id,
 *     gymId: comment.gymId,
 *     reason: 'harassment',
 *     details: 'This comment is targeting me personally',
 *   });
 * };
 * ```
 */
export const useContentFlag = (): UseContentFlagResult => {
  const { user, userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  const submitFlag = useCallback(
    async (data: {
      contentType: FlagContentType;
      contentId: string;
      gymId: string;
      reason: FlagReason;
      details?: string;
    }): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to report content');
      }

      // Validate details length
      if (data.details && data.details.length > 500) {
        throw new Error('Details must be 500 characters or less');
      }

      setIsSubmitting(true);
      setError(null);
      setSuccess(false);

      try {
        const db = getFirebaseDb();
        const flagsRef = collection(db, 'flags');

        const displayName = userProfile?.displayName ?? user.displayName ?? 'Anonymous';

        const flagData: ContentFlagCreateData & {
          status: 'pending';
          createdAt: ReturnType<typeof serverTimestamp>;
        } = {
          contentType: data.contentType,
          contentId: data.contentId,
          gymId: data.gymId,
          reporterId: user.uid,
          reporterDisplayName: displayName,
          reason: data.reason,
          ...(data.details && { details: data.details.trim() }),
          status: 'pending',
          createdAt: serverTimestamp(),
        };

        await addDoc(flagsRef, flagData);
        setSuccess(true);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to submit report';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, userProfile]
  );

  return {
    submitFlag,
    isSubmitting,
    error,
    success,
    reset,
  };
};

export default useContentFlag;
